// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  type AppError,
  Authn,
  AuthnMethod,
  ErrorCode,
  type EventPayload,
  type FeatureFlagsProp,
} from '@imprivata-cloud/authn';
import {
  addSpanEvent,
  createSpanName,
  debug,
  error,
  ImprHeaders,
  instrument,
  SpanComponent,
  withAction,
} from '@imprivata-cloud/common';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { Banner } from '@imprivata-cloud/design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { endWorkflowAndCloseWindow, hideWindow, useAgent } from '@/api/agent';
import {
  getOrgPreferences,
  handleAuthnArtifact,
  setDefaultHeader,
} from '@/api/api';
import DefaultLogofrom from '@/assets/default_logo.svg?react';
import { QueryParam } from '@/constants';
import { AuthnLayout, LayoutHeaders } from '@/containers/Card/authn-layout';
import { UserNameForm } from '@/factors/username-form';
import { WEB_APP_NAME } from '@/shared/constants';
import { getWorkflowId } from '@/tracing';
import { getUrlParamRaw } from '@/utils';

import { useBanner } from './hooks/useBanner';
import { loginHintOf, useEventHandler } from './hooks/useEventHandler';
import { useQueryParams } from './hooks/useQueryParams';
import {
  headerOf,
  hexOf,
  INITIAL_VIEW,
  reduceView,
  showsFaceId,
  showsInformationDialog,
  type ViewState,
} from './hooks/viewState';
import { wait } from './utils';

import './styles.less';

import { InformationDialog } from '@/errorHandler/error-views/no-inline-enrollment';

const onSuccessSpan = createSpanName(SpanComponent.UI, 'service', 'on-success');
const onFailureSpan = createSpanName(SpanComponent.UI, 'service', 'on-failure');
const DEFAULT_FAILURE_REDIRECT_DELAY_MS = 100;
const DEVICE_INITIALIZATION_TIMEOUT_MS = 10_000;

const buildArtifactRedirectUrl = (
  {
    redirectUri,
    authorizationCode,
    errorCode,
    errorDescription,
    state,
  }: Awaited<ReturnType<typeof handleAuthnArtifact>>,
  loginHint?: string,
) => {
  let url = redirectUri;

  debug('[login-hint] buildArtifactRedirectUrl', {
    hasAuthorizationCode: !!authorizationCode,
    errorCode,
    loginHint,
  });

  if (authorizationCode) {
    url += `?code=${authorizationCode}`;
  } else if (errorCode) {
    url += `?error=${errorCode}`;
    if (errorDescription) {
      url += `&error_description=${errorDescription}`;
    }
    // On the failure exit, echo the attempted username back to the RP so it can
    // pre-fill its login form on retry.
    if (loginHint) {
      url += `&login_hint=${encodeURIComponent(loginHint)}`;
    }
  }

  if (state) {
    url += `&state=${state}`;
  }

  return url;
};

const DEFAULT_LOGO = <DefaultLogofrom />;

const DEFAULT_DEVICE_FACTORS: AuthnMethod[] = [
  AuthnMethod.FACE_ID,
  AuthnMethod.PASSWORD,
  AuthnMethod.IMPR_ID,
  AuthnMethod.IMPR_PIN,
  AuthnMethod.TEMP_CODE,
  AuthnMethod.EMAIL_OTP,
];

interface FlowHandlerOptions {
  rpRequestId: string;
  onFinished: (b: boolean) => void;
}

const onSuccessHandler = instrument(
  onSuccessSpan,
  withAction(
    'Redirection',
    async ({
      authorizationCode,
      rpRequestId,
      onFinished,
    }: {
      authorizationCode: string;
    } & FlowHandlerOptions) => {
      try {
        debug('Processing authentication with code');

        const response = await handleAuthnArtifact({
          rpRequestId,
          authorizationCode,
        });

        debug('Authentication artifact processed successfully', {
          redirectUri: response.redirectUri,
          hasState: !!response.state,
          hasAuthorizationCode: !!response.authorizationCode,
        });

        onFinished(true);
        hideWindow();

        addSpanEvent(createSpanName(onSuccessSpan, 'redirect'), {
          redirectUri: response.redirectUri,
          state: response.state,
        });

        const redirectUrl = buildArtifactRedirectUrl(response);

        debug('Redirecting to relying party', { redirectUrl });

        window.location.href = redirectUrl;
      } catch (err) {
        error('Authentication failed', String(err));
        throw err; // Let the instrumentation handle the error
      }
    },
  ),
);

const onFailureHandler = instrument(
  onFailureSpan,
  withAction(
    'Redirection',
    async ({
      rpRequestId,
      redirectDelayMs = DEFAULT_FAILURE_REDIRECT_DELAY_MS,
      errorCode,
      errorDescription,
      loginHint,
      onFinished,
    }: {
      redirectDelayMs?: number;
      errorCode?: string;
      errorDescription?: string;
      loginHint?: string;
    } & FlowHandlerOptions) => {
      try {
        debug('Processing authentication failure');

        const response = await handleAuthnArtifact({
          rpRequestId,
          errorCode,
          errorDescription,
        });

        debug('Authentication artifact processed with failure response', {
          redirectUri: response.redirectUri,
          hasState: !!response.state,
          hasErrorCode: !!response.errorCode,
          hasErrorDescription: !!response.errorDescription,
        });

        onFinished(true);

        addSpanEvent(createSpanName(onFailureSpan, 'redirect'), {
          redirectUri: response.redirectUri,
          state: response.state,
          errorCode: response.errorCode,
          errorDescription: response.errorDescription,
        });

        // EAM exit
        // TODO: Verify with EAM
        endWorkflowAndCloseWindow();

        // ...or Redirect exit
        const redirectUrl = buildArtifactRedirectUrl(response, loginHint);

        debug('Redirecting to relying party', {
          redirectUrl,
          redirectDelayMs,
          errorCode,
        });
        if (redirectDelayMs > 0) {
          await wait(redirectDelayMs);
        }

        window.location.href = redirectUrl;
      } catch (err) {
        error('Authentication failure handling failed', String(err));
        throw err; // Let the instrumentation handle the error
      }
    },
  ),
);

export const IcpCoreFlow: React.FC = () => {
  const { t } = useTranslation();

  // Isolated EAM Agent Web API code. This hook must contain all and every instance of those Web APIs
  const showUI = useAgent();

  // Track showUI transitions so the transient banner is cleared when the EAM
  // window is shown again after being hidden.
  const prevShowUIRef = useRef(showUI);

  const { banner, setBanner, displayBanner } = useBanner();

  const clearBanner = useCallback(() => setBanner(undefined), [setBanner]);

  const {
    initialFactors,
    oidcRequestData,
    applicationId,
    tenantId,
    rpRequestId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
  } = useQueryParams();

  const [username, setUsername] = useState<string | undefined>(
    () => getUrlParamRaw(QueryParam.LoginHint) ?? '',
  );

  // The Authn sub-machine's presentation state. Header, hex glyph and layout
  // flags are pure projections of this single value (see viewState).
  const [view, setView] = useState<ViewState>(INITIAL_VIEW);
  const dispatchView = useCallback(
    (payload: EventPayload) => setView(v => reduceView(v, payload)),
    [],
  );

  const [finished, setFinished] = useState(false);
  const [logoUrl, setLogoUrl] = useState<React.ReactNode>(DEFAULT_LOGO);
  const [screenError, setScreenError] = useState<AppError | undefined>(
    undefined,
  );
  /**
   * Authn Props
   */
  const deviceFactors = useMemo(() => {
    if (!initialFactors) {
      return DEFAULT_DEVICE_FACTORS;
    }
    // For 1:N flows, face is the only factor (no enroll/consent needed)
    if (isOneToMany) {
      return [AuthnMethod.FACE_ID];
    }
    // We need PASSWORD factor when using "face" since we could need to ENROLL/CONSENT
    if (
      initialFactors.some(factor => factor.factorType === AuthnMethod.FACE_ID)
    ) {
      return [AuthnMethod.FACE_ID, AuthnMethod.PASSWORD];
    }
    return initialFactors.map(factor => factor.factorType);
  }, [initialFactors, isOneToMany]);

  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username: isOneToMany ? undefined : username,
      oidcRequestData,
    }),
    [oidcRequestData, username, isOneToMany],
  );

  const tracing = useMemo(() => ({ workflowId: getWorkflowId() ?? '' }), []);

  // Both terminal exits (success redirect, failure redirect/close) complete the
  // flow exactly once. A single latch makes the underlying handleAuthnArtifact
  // call idempotent across StrictMode's double-invoked effects and any duplicate
  // event; it is released only when the call rejects so a later attempt can run.
  const redirectInitiatedRef = useRef(false);

  const onSuccess = useCallback(
    (
      opts: {
        authorizationCode: string;
      } & Omit<FlowHandlerOptions, 'rpRequestId' | 'onFinished'>,
    ) => {
      if (redirectInitiatedRef.current) {
        debug('Redirect already initiated; ignoring duplicate onSuccess');
        return;
      }
      redirectInitiatedRef.current = true;
      // The handler rethrows so instrument records the span error; catch here to
      // keep the memoized callback from leaking an unhandled rejection.
      return onSuccessHandler({
        rpRequestId,
        onFinished: setFinished,
        ...opts,
      }).catch(err => {
        redirectInitiatedRef.current = false;
        error('Authentication success redirect failed', String(err));
        // TODO: surface a terminal error screen here once the design is known.
      });
    },
    [rpRequestId],
  );

  const onFailure = useCallback(
    (
      opts: {
        redirectDelayMs?: number;
        errorCode?: string;
        errorDescription?: string;
        loginHint?: string;
      } & Omit<FlowHandlerOptions, 'rpRequestId' | 'onFinished'>,
    ) => {
      if (redirectInitiatedRef.current) {
        debug('Redirect already initiated; ignoring duplicate onFailure');
        return;
      }
      redirectInitiatedRef.current = true;
      // The handler rethrows so instrument records the span error; catch here to
      // keep the memoized callback from leaking an unhandled rejection.
      return onFailureHandler({
        rpRequestId,
        onFinished: setFinished,
        ...opts,
      }).catch(err => {
        redirectInitiatedRef.current = false;
        error('Authentication failure redirect failed', String(err));
        // TODO: surface a terminal error screen here once the design is known.
      });
    },
    [rpRequestId],
  );

  // Terminal error screens (account locked/disabled, enrollment
  // unsupported) are dead ends for this session: acknowledging them fails the
  // flow back to the RP with the cause rather than dismissing locally.
  const handleTerminalScreenFailure = useCallback(
    (errorCode: string, errorDescription: string) =>
      onFailure({
        errorCode,
        errorDescription,
        loginHint: screenError ? loginHintOf(screenError) : undefined,
      }),
    [onFailure, screenError],
  );

  const errorScreens: Partial<Record<ErrorCode, React.ReactElement>> = useMemo(
    () => ({
      [ErrorCode.ACCOUNT_LOCKED]: (
        <InformationDialog
          text={t('errors.idp-locked')}
          title={t('errors.account-locked-title')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.ACCOUNT_LOCKED,
                t('errors.idp-locked'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT]: (
        <InformationDialog
          title={t('errors.face-not-enrolled-title')}
          text={t('errors.face-not-enrolled-text')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT,
                t('errors.face-not-enrolled-text'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.ACCOUNT_DISABLED]: (
        <InformationDialog
          text={t('errors.account-disabled')}
          title={t('errors.account-locked-title')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.ACCOUNT_DISABLED,
                t('errors.account-disabled'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.UNSUPPORTED_OPERATION]: (
        <InformationDialog
          text={t('errors.something-unexpected')}
          buttonProps={{
            onClick: () =>
              handleTerminalScreenFailure(
                ErrorCode.UNSUPPORTED_OPERATION,
                t('errors.something-unexpected'),
              ),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
    }),
    [t, handleTerminalScreenFailure],
  );

  const onCancel = useCallback(() => {
    debug('Authentication canceled by user');
    onFailure({ redirectDelayMs: 0, errorCode: 'user_cancelled' });
  }, [onFailure]);

  const handleEvent = useEventHandler({
    onSuccess,
    onFailure,
    onCancel,
    setUsername,
    displayBanner,
    clearBanner,
    setScreenError,
    dispatchView,
  });

  /**
   * EAM specific `showUI` handling effect
   */
  useEffect(() => {
    debug('showUI', showUI);
    if (showUI && !prevShowUIRef.current) {
      debug('Window re-shown after hide, clearing banner');
      clearBanner();
    }
    prevShowUIRef.current = showUI;
  }, [showUI, clearBanner]);

  /**
   * Initial setup effect - setting headers, fetching org logo, etc.
   *
   * TODO: Identify what can be stripped out, removed
   */
  useEffect(() => {
    setDefaultHeader(
      ImprHeaders.ImprClientName,
      jsonToBase64({ name: WEB_APP_NAME }),
    );
    setDefaultHeader(ImprHeaders.ImprTenantId, tenantId);
    setLogoUrl(DEFAULT_LOGO);

    if (tenantId) {
      void (async () => {
        try {
          const { logoUrl: orgLogoUrl } = await getOrgPreferences();
          if (orgLogoUrl) {
            setLogoUrl(p =>
              typeof p === 'string' && p === orgLogoUrl ? p : orgLogoUrl,
            );
          }
        } catch (e) {
          debug('Failed to fetch org logo, using default', { error: e });
        }
      })();
    }
  }, [tenantId]);

  // Scroll the page up when virtual keyboard is hidden (input loses focus)
  useEffect(() => {
    // Fixes an issue on mobile when, after virtual keyboard is hidden, the page
    // remains scrolled down a bit and there is no way to scroll it up because
    // the container has "overflow: hidden" and there is no scroll bar
    const scrollHandler = () => {
      window.scrollTo(0, 0);
    };
    // 'focusout' is like 'blur' but bubbles, so it can be caught on 'window'
    window.addEventListener('focusout', scrollHandler);
    return () => {
      window.removeEventListener('focusout', scrollHandler);
    };
  }, []);

  const header = headerOf(view) ?? LayoutHeaders.AUTHENTICATION;
  const hexImage = hexOf(view);
  const isInformationDialog = showsInformationDialog(view);
  const isFaceId = showsFaceId(view);

  const renderCustomBanner =
    banner && isFaceId ? (
      <Banner
        message={banner?.subMessage}
        title={banner?.message}
        type={
          banner?.status === 'error' || banner?.status === 'warning'
            ? 'warning'
            : 'success'
        }
        className="icp-flow-face-banner"
      />
    ) : undefined;

  const featureFlags: FeatureFlagsProp = useMemo(() => {
    // TODO: determine based on tenantId once we decide what to do with IAD
    const flags: FeatureFlagsProp = { enableIAD: false };
    if (fitVideoFrame) {
      flags.fitVideoFrame = fitVideoFrame;
    }
    flags.deviceInitTimeoutMs = DEVICE_INITIALIZATION_TIMEOUT_MS;
    return flags;
  }, [fitVideoFrame]);

  if (finished || !showUI) {
    return null;
  }

  if (screenError) {
    return errorScreens[screenError.code as ErrorCode] ?? null;
  }

  return (
    <AuthnLayout
      banner={banner}
      hexImage={hexImage}
      header={header}
      logoUrl={logoUrl}
      hideLayout={isInformationDialog}
      renderCustomBanner={renderCustomBanner}
    >
      {!username && !isOneToMany && (
        <UserNameForm onSubmit={setUsername} initValue={username} />
      )}
      {(username || isOneToMany) && (
        <Authn
          contextResource={contextResource}
          tenantId={tenantId}
          applicationId={applicationId}
          onEvent={handleEvent}
          deviceFactors={deviceFactors}
          factors={initialFactors}
          requestConfig={requestConfig}
          tracing={tracing}
          hideFactorSwitch={hideFactorSwitch}
          featureFlags={featureFlags}
          allowChangeUsername={!loginHint}
        />
      )}
    </AuthnLayout>
  );
};
